type LogLevel = 'error' | 'warn' | 'info';

type LogContext = Record<string, unknown>;

type DDLogsModule = typeof import('@datadog/browser-logs');

let logModule: DDLogsModule | null = null;
let loadPromise: Promise<DDLogsModule | null> | null = null;

function getDatadogLogs(): Promise<DDLogsModule | null> | null {
  if (typeof window === 'undefined' || !process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID) {
    return null;
  }

  if (logModule) return loadPromise;

  if (!loadPromise) {
    loadPromise = import('@datadog/browser-logs')
      .then((mod) => {
        logModule = mod;
        return mod;
      })
      .catch(() => {
        // Reset so next call retries the import
        loadPromise = null;
        return null;
      });
  }

  return loadPromise;
}

function log(level: LogLevel, message: string, context?: LogContext): void {
  const ddPromise = getDatadogLogs();

  if (ddPromise) {
    ddPromise.then((mod) => {
      mod?.datadogLogs.logger[level](message, context);
    });
  } else if (process.env.NODE_ENV === 'development') {
    // Fallback to console in development where Datadog is not initialized
    // biome-ignore lint/suspicious/noConsole: intentional dev-only fallback
    const consoleFn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.info;
    consoleFn(`[DD:${level}] ${message}`, context);
  }
}

/**
 * Centralized logger that sends logs to Datadog Browser Logs.
 *
 * Only use for things the backend cannot see:
 * - Auth / session anomalies (client-side token failures)
 * - Critical multi-step business op failures (step 1 OK, step 2 fails)
 * - Network-level failures (request never reached the server)
 *
 * Do NOT use for generic API errors — the backend already logs those.
 *
 * @example
 * logger.error('[Auth] Token refresh failed', { userId, reason });
 * logger.warn('[SidePanel] Opened with no work order context', { pathname });
 */
/**
 * Normalizes unknown catch values to a string message.
 *
 * Rejected thunks reject with a plain object (`{ message, status, actionType }`),
 * not an `Error`, so a bare `String(error)` logged `"[object Object]"` and threw the
 * only useful detail away. Prefer a `message`/`error` field, then fall back to a
 * JSON dump before `String()`.
 */
/**
 * Datadog bills by volume and every value here is an arbitrary API error body — a
 * bulk validation failure can carry a field-by-field map for every row submitted.
 * Capped rather than field-allowlisted: the JSON branch only runs because no
 * `message` / `error` string was found, so there is no known-good subset left to
 * pick, and the head of the payload is where the useful part is.
 */
const MAX_NORMALIZED_ERROR_LENGTH = 512;

function capForLogging(value: string): string {
  if (value.length <= MAX_NORMALIZED_ERROR_LENGTH) return value;
  const droppedCharacters = value.length - MAX_NORMALIZED_ERROR_LENGTH;
  return `${value.slice(0, MAX_NORMALIZED_ERROR_LENGTH)}… [truncated ${droppedCharacters} chars]`;
}

export function normalizeError(error: unknown): string {
  if (error instanceof Error) return capForLogging(error.message);

  if (error && typeof error === 'object') {
    const candidate = error as { message?: unknown; error?: unknown };
    if (typeof candidate.message === 'string' && candidate.message) {
      return capForLogging(candidate.message);
    }
    if (typeof candidate.error === 'string' && candidate.error) {
      return capForLogging(candidate.error);
    }

    try {
      const serialized = JSON.stringify(error);
      if (typeof serialized === 'string') return capForLogging(serialized);
    } catch {
      // Circular or otherwise unserializable — fall through to String().
    }
  }

  return capForLogging(String(error));
}

export const logger = {
  error(message: string, context?: LogContext) {
    log('error', message, context);
  },
  warn(message: string, context?: LogContext) {
    log('warn', message, context);
  },
  info(message: string, context?: LogContext) {
    log('info', message, context);
  },
};
